import { lazy, useCallback, useLayoutEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { MotionConfig } from "framer-motion";
import "./App.css";
import { getTheme } from "./styles/theme";
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { ParallaxProvider } from "react-scroll-parallax";
import { getInitialThemeMode, ThemeMode, ThemeModeContext, THEME_MODE_STORAGE_KEY } from "./styles/themeMode";
const DashboardContainer = lazy(() => import("./module/Dashboard/DashboardContainer"));

function App() {
  const [mode, setMode] = useState<ThemeMode>(getInitialThemeMode);
  const theme = useMemo(() => getTheme(mode), [mode]);

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const toggleMode = useCallback(
    (origin?: { x: number; y: number }) => {
      const next: ThemeMode = mode === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(THEME_MODE_STORAGE_KEY, next);
      } catch (e) {
        // storage unavailable, theme just won't persist
      }
      const doc = document as any;
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (!doc.startViewTransition || reduceMotion) {
        setMode(next);
        return;
      }
      // circular reveal of the new theme, starting from the toggle button
      const root = document.documentElement;
      root.style.setProperty("--ww-vt-x", `${origin?.x ?? window.innerWidth}px`);
      root.style.setProperty("--ww-vt-y", `${origin?.y ?? 0}px`);
      doc.startViewTransition(() => flushSync(() => setMode(next)));
    },
    [mode],
  );

  const themeModeValue = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeModeContext.Provider value={themeModeValue}>
        <MuiThemeProvider theme={theme}>
          <StyledEngineProvider injectFirst>
            <MotionConfig reducedMotion="user">
              <ParallaxProvider>
                <DashboardContainer />
              </ParallaxProvider>
            </MotionConfig>
          </StyledEngineProvider>
        </MuiThemeProvider>
      </ThemeModeContext.Provider>
    </StyledEngineProvider>
  );
}

export default App;
